import clsx, { ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { format, isValid, differenceInDays } from 'date-fns';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(...inputs));
}

export const mongolian_alphabet = [
  { id: 'а', value: 'А' },
  { id: 'б', value: 'Б' },
  { id: 'в', value: 'В' },
  { id: 'г', value: 'Г' },
  { id: 'д', value: 'Д' },
  { id: 'е', value: 'Е' },
  { id: 'ё', value: 'Ё' },
  { id: 'ж', value: 'Ж' },
  { id: 'з', value: 'З' },
  { id: 'и', value: 'И' },
  { id: 'й', value: 'Й' },
  { id: 'к', value: 'К' },
  { id: 'л', value: 'Л' },
  { id: 'м', value: 'М' },
  { id: 'н', value: 'Н' },
  { id: 'о', value: 'О' },
  { id: 'ө', value: 'Ө' },
  { id: 'п', value: 'П' },
  { id: 'р', value: 'Р' },
  { id: 'с', value: 'С' },
  { id: 'т', value: 'Т' },
  { id: 'у', value: 'У' },
  { id: 'ү', value: 'Ү' },
  { id: 'ф', value: 'Ф' },
  { id: 'х', value: 'Х' },
  { id: 'ц', value: 'Ц' },
  { id: 'ч', value: 'Ч' },
  { id: 'ш', value: 'Ш' },
  { id: 'щ', value: 'Щ' },
  { id: 'ъ', value: 'Ъ' },
  { id: 'ы', value: 'Ы' },
  { id: 'ь', value: 'Ь' },
  { id: 'э', value: 'Э' },
  { id: 'ю', value: 'Ю' },
  { id: 'я', value: 'Я' },
];

export const chunks = (arr: any[], size: number) => {
  return Array.from({ length: Math.ceil(arr.length / size) }, (v, i) =>
    arr.slice(i * size, i * size + size)
  );
};

export const ensureHttpProtocol = (url: string): string => {
  if (!url) return url;

  const trimmedUrl = url.trim();

  if (/^https?:\/\//i.test(trimmedUrl)) {
    return trimmedUrl;
  }

  return `https://${trimmedUrl}`;
};

export const companyLogo = (logo: string, companyId: number) => {
  if (!logo) return '/images/company.svg';
  return `${process.env.NEXT_PUBLIC_CDN_URL}/logo/f/${Math.ceil(companyId / 2000)}/${logo}.png`;
};

export const safeFormat = (
  value: string | Date | null | undefined,
  formatStr: string
) => {
  if (!value) return '';
  const date = new Date(value);
  return isValid(date) ? format(date, formatStr) : '';
};

export async function getUserLocationReal(): Promise<{
  lat: number;
  lon: number;
} | null> {
  return new Promise((resolve) => {
    if (!('geolocation' in navigator)) {
      resolve(null);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        resolve({
          lat: pos.coords.latitude,
          lon: pos.coords.longitude,
        });
      },
      () => {
        resolve(null);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      }
    );
  });
}

export const isExpiredDateEndors = (date: number) => {
  if (date <= 0) return false;
  const dateObj = new Date(date * 1000);
  const today = new Date();
  const daysDiff = differenceInDays(today, dateObj);
  return daysDiff >= 30;
};

/**
 * date format function
 * input:
 *  - unix time (seconds)
 *  - unix timestamp (milliseconds)
 *  - string
 *  - Date
 *  - null / undefined
 *
 * output:
 *  - formatted string
 *
 * options:
 *  - formatString (default: 'YYYY-MM-DD HH:mm:ss')
 *
 * fallback:
 *  - return original input
 */

export const formatDate = (
  input: string | number | Date | null | undefined,
  options: {
    formatString?: string;
  } = {
    formatString: 'YYYY-MM-DD',
  }
): any => {
  if (input === null || input === undefined) {
    return input;
  }

  let date;

  // Date object
  if (input instanceof Date) {
    date = input;
  }

  // number
  else if (typeof input === 'number') {
    // unix time (seconds)
    if (input < 1000000000000) {
      date = new Date(input * 1000);
    }
    // unix timestamp (milliseconds)
    else {
      date = new Date(input);
    }
  }

  // string
  else if (typeof input === 'string') {
    const trimmed = input.trim();

    // numeric string
    if (/^\d+$/.test(trimmed)) {
      const num = Number(trimmed);

      if (trimmed.length <= 10) {
        date = new Date(num * 1000);
      } else {
        date = new Date(num);
      }
    } else {
      date = new Date(trimmed);
    }
  }

  // unsupported type
  else {
    return input;
  }

  // invalid date
  if (!(date instanceof Date) || isNaN(date.getTime())) {
    return input;
  }

  const YYYY = date.getFullYear();
  const MM = String(date.getMonth() + 1).padStart(2, '0');
  const DD = String(date.getDate()).padStart(2, '0');

  const HH = String(date.getHours()).padStart(2, '0');
  const mm = String(date.getMinutes()).padStart(2, '0');
  const ss = String(date.getSeconds()).padStart(2, '0');

  return options?.formatString
    ? options.formatString
        .replace(/YYYY/g, String(YYYY))
        .replace(/MM/g, MM)
        .replace(/DD/g, DD)
        .replace(/HH/g, HH)
        .replace(/mm/g, mm)
        .replace(/ss/g, ss)
    : date;
};
